import React, { useEffect, useState } from 'react';
import { WebhooksTableRow } from '../../components/developers/webhooks_page/WebhooksTableRow';
import { useNavigate } from 'react-router-dom';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { BASE_URL } from '../../../config/environmentConfig';
import { useTranslation } from 'react-i18next';
import { fetchWebhooksList } from '../../api/webhook-api-service';
import { Title } from '../../components/developers/page-components/Title';
import { TableComponent } from '../../components/TableComponent';
import { useLocation } from 'react-router';
import { LinkComponent } from '../../components/LinkComponent';
import { deleteWebhook } from '../../store/merchnts/actions/webhooks';
import { CLAIMS } from '../../utils/roles-helper';
import { HmacSigningKey } from '../../components/developers/HmacSigningKey';

export const COLUMNS = [
	{
		key: 'url',
		title: 'WEBHOOKS.URL',
		additionalColumnClassName: 'w-100px w-md-300px w-xl-400px w-xxl-600px',
		claims: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN
	},
	{
		key: 'event',
		title: 'WEBHOOKS.EVENT',
		additionalColumnClassName: '',
		claims: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN
	},
	{
		key: 'actions',
		title: 'WEBHOOKS.ACTIONS',
		additionalColumnClassName: 'w-175px',
		claims: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN
	}
];

export const WebhooksList = (): JSX.Element => {
	const { pathname } = useLocation();
	const [subscriberKey, setSubscriberKey] = useState<string>('');
	const [webhooks, setWebhooks] = useState([]);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const refetchData = async (): Promise<void> => {
		try {
			setLoading(true);

			const data = await fetchWebhooksList();

			setWebhooks(data);

			if (data?.length && data?.[0]?.subscriberKey) {
				setSubscriberKey(data?.[0]?.subscriberKey);
			}

		} catch (e) {
			console.log(e);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void refetchData();
	}, []);

	const closeModal = (): void => {
		setModalState(prevState => ({
			...prevState,
			isOpen: false
		}));
	};

	const onDelete = (webhookId?: string): void => {
		if (webhookId) {
			setModalState({
        isOpen: true,
				modalTitle: t('WEBHOOKS.DELETE_TITLE'),
				modalText: t('WEBHOOKS.DELETE_TEXT'),
				onConfirm: async () => {
					await deleteWebhook(webhookId);
					closeModal();
					await refetchData();
				},
				onCancel: closeModal
			});

		}
	};

	return (
		<>
			<div className="card mb-5">
				<div className="card-body">
					<Title title={t('WEBHOOKS.WEBHOOKS')} additionalClass="fs-2 mb-5"/>
					<div className="d-sm-flex justify-content-sm-between align-items-sm-end pt-3">
						<div className="fs-6 col-sm-8">
							{t('WEBHOOKS.ABOUT_TEXT')}
						</div>
						<div className="col-sm-4 text-sm-end mt-5 mt-sm-0">
							<LinkComponent
								link={BASE_URL.WEBHOOK_DOCS}
								text={t('WEBHOOKS.READ_DOC')}
								additionalClass="fs-8 fw-bolder"
							/>
						</div>
					</div>
				</div>
			</div>

			<HmacSigningKey subscriberKey={subscriberKey} loading={loading} />

			<div className="card">
				<TableComponent
					column={COLUMNS}
					tableData={webhooks}
					isDataLoading={loading}
					title={t('WEBHOOKS.ENDPOINTS')}
					buttonName={t('WEBHOOKS.BUTTON_TEXT')}
					onButtonClick={(): void => navigate(`${pathname}/create-endpoint`)}
					getRowComponent={(itemProps): JSX.Element => (
						<WebhooksTableRow
							{...itemProps}
							onDeleteClick={(): void => onDelete(itemProps.id)}
							key={itemProps.id}
						/>
					)}
				/>
				<ModalWindow
					{...modalState}
					onClose={closeModal}
				/>
			</div>
		</>
	);
};
